import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, MapPin, Phone } from "lucide-react";
import { Reveal } from "./Reveal";
import { getInterestOptions } from "@/data/content";
import { UI } from "@/i18n/copy";
import { useLang } from "@/i18n/LanguageContext";

const CONTACT_ENDPOINT = process.env.REACT_APP_CONTACT_ENDPOINT || "/contact.php";

const INFO_ICONS = [Phone, Mail, MapPin];

export const ContactSection = () => {
  const { lang } = useLang();
  const t = UI[lang].contact;
  const options = getInterestOptions(lang);

  const EMPTY_FORM = { name: "", email: "", company: options[0], message: "", website: "" };
  const [form, setForm] = useState(EMPTY_FORM);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(CONTACT_ENDPOINT, form);
      if (!data || data.success !== true) throw new Error("send failed");
      toast.success(t.success);
      setForm({ ...EMPTY_FORM, company: form.company });
    } catch (err) {
      toast.error(t.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-black/15 bg-white px-4 py-3.5 text-sm text-black outline-none transition-colors placeholder:text-black/35 focus:border-black";

  return (
    <section id="contact" data-testid="contact-section" className="scroll-mt-20 border-t border-black/10 bg-white">
      <div className="mx-auto grid max-w-7xl gap-16 px-5 py-24 sm:px-8 sm:py-32 lg:grid-cols-2">
        <Reveal>
          <p className="font-mono-label text-[11px] uppercase tracking-[0.3em] text-black/50">
            {t.eyebrow}
          </p>
          <h2 className="mt-6 text-4xl font-black uppercase leading-[1] tracking-tight text-black sm:text-5xl lg:text-6xl">
            {t.headingA}{" "}
            {lang === "en" ? (
              <span className="font-script normal-case tracking-normal">{t.accent}</span>
            ) : (
              t.accent
            )}
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-black/55">{t.sub}</p>

          <div className="mt-10 space-y-5">
            {t.info.map((p, i) => {
              const Icon = INFO_ICONS[i];
              return (
                <div key={p.label} className="flex items-center gap-4" data-testid={`contact-info-${i}`}>
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-black/15">
                    <Icon className="h-4 w-4 text-black" />
                  </span>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-black">{p.label}</span>
                      {p.note && (
                        <span className="rounded-full bg-black/5 px-2 py-0.5 font-mono-label text-[10px] uppercase tracking-[0.15em] text-black/45">
                          {p.note}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-black/55">{p.value}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="rounded-3xl border border-black/10 bg-zinc-50 p-8 sm:p-10"
          >
            <div className="grid gap-5">
              <input
                type="text"
                name="website"
                data-testid="contact-website-honeypot"
                value={form.website}
                onChange={set("website")}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
              />
              <div>
                <label htmlFor="contact-name" className="mb-2 block text-[12px] font-bold uppercase tracking-[0.14em] text-black">
                  {t.labels.name}
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder={t.placeholders.name}
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-2 block text-[12px] font-bold uppercase tracking-[0.14em] text-black">
                  {t.labels.email}
                </label>
                <input
                  id="contact-email"
                  data-testid="contact-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder={t.placeholders.email}
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-company" className="mb-2 block text-[12px] font-bold uppercase tracking-[0.14em] text-black">
                  {t.labels.company}
                </label>
                <select
                  id="contact-company"
                  data-testid="contact-company-select"
                  value={form.company}
                  onChange={set("company")}
                  className={inputCls}
                >
                  {options.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="contact-message" className="mb-2 block text-[12px] font-bold uppercase tracking-[0.14em] text-black">
                  {t.labels.message}
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  required
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  placeholder={t.placeholders.message}
                  className={`${inputCls} resize-none`}
                />
              </div>
              <button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={sending}
                className="group mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-black px-8 py-4 text-[13px] font-bold uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:bg-black/80 disabled:opacity-50"
              >
                {sending ? t.sending : t.send}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 rtl:-scale-x-100" />
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
